import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@mui/material';
import ButtonLoading from '@/components/common/ButtonLoading';
import { useSelector } from 'react-redux';
import {
    useCheckAuthState,
    useToastNotification,
    useDocumentFormData,
    useSubscription
} from '@/hooks';
import {
    useSaveRecurringInvoice,
    useValidateRecurringInvoice
} from '@/hooks/useSidebarActions';
import { SaveIcon } from '@/resources/icons';
import { documentSaved as posthogDocumentSaved } from '@/modules/posthog/posthogManager';

import useStyles from '../../styles';

const RecurringInvoiceSave = ({ onNegativeInvoice, onLoginBlocker }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);

    const { loadingUser, registeredUser } = useCheckAuthState();
    const saveRecurringInvoice = useSaveRecurringInvoice();
    const { documentLoading, total, locked } = useDocumentFormData();
    const validateRecurringInvoice = useValidateRecurringInvoice();
    const { triggerToast } = useToastNotification();
    const subscriptionData = useSubscription();

    // Used for validation.
    const invoice = useSelector((state) => state?.makeInvoice);

    const blockCreation = !subscriptionData?.components?.recurring?.enabled;

    const handleOnClick = async () => {
        setLoading(true);
        const invoiceData = await validateRecurringInvoice(invoice);

        if (!invoiceData) {
            setLoading(false);
            return;
        }

        if (total < 0) {
            onNegativeInvoice();
            setLoading(false);
            return;
        }

        const completeSaveProcess = async () => {
            await saveRecurringInvoice(invoiceData);

            if (!blockCreation) {
                triggerToast({
                    message: 'Recurring Invoice Saved',
                    action: 'saved',
                    icon: <SaveIcon />
                });
            }

            posthogDocumentSaved(invoiceData);
            setLoading(false);
        };

        // Check is a user is registered, if they are complete the process.
        // If not, see if we can get them to register.
        if (registeredUser) {
            completeSaveProcess();
        } else {
            onLoginBlocker(completeSaveProcess);
        }
    };

    const loadingButton = loadingUser || documentLoading;

    if (!locked) {
        return (
            <div className={classes.section}>
                {!loadingButton ? (
                    <ButtonLoading
                        fullWidth
                        className={classes.button}
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<SaveIcon className={classes.buttonSaveSvg} />}
                        onClick={handleOnClick}
                        disableOnLoading
                        loading={loading}
                    >
                        Save
                    </ButtonLoading>
                ) : (
                    <Skeleton variant="rectangular" className={classes.sizer} />
                )}
            </div>
        );
    }
    return null;
};

RecurringInvoiceSave.propTypes = {
    onNegativeInvoice: PropTypes.func.isRequired,
    onLoginBlocker: PropTypes.func.isRequired
};

export default RecurringInvoiceSave;
